<template>
  <v-row>
    <v-col>
      <v-stepper v-model="step">
        <v-stepper-header>
          <v-stepper-step :complete="step > 1" step="1">{{ $t('選取照片') }}</v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 2" step="2">{{ $t('送件') }}</v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <v-alert
              color="primary"
              icon="mdi-information"
              text
            >
              {{ $t('為了上傳更有效率，建議請將圖檔尺寸在 1200x1200 內。') }}
            </v-alert>
            <h2
              v-if="files.length == 0"
              class="text-center py-12 grey lighten-5 placeholder"
              @click="$refs.fileInput.click()"
            >
              <v-icon left large>mdi-cloud-upload-outline</v-icon> {{ $t('點擊上傳或是拖曳照片到畫面中') }}
              <input type="file" accept="image/png,image/jpeg" multiple ref="fileInput" @change="onFileChange">
            </h2>
            <v-simple-table
              v-else
              height="60vh"
              fixed-header
            >
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">{{ $t("檔名") }}</th>
                    <th class="text-left">{{ $t("檔案大小") }}</th>
                    <th class="text-center" width="120">{{ $t("動作") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(file, index) in files" :key="index">
                    <td>{{ file.name }}</td>
                    <td>{{ filesize(file.size) }}</td>
                    <td class="text-center">
                      <v-btn
                        small
                        outlined
                        color="primary"
                        @click="removeFile(index)"
                      >
                        <v-icon>mdi-delete-outline</v-icon>
                        {{ $t('刪除') }}
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <images-dropper @drop="onDrop"></images-dropper>

            <div class="d-flex align-baseline justify-space-between mt-12">
              <v-btn
                large
                depressed
                @click="reset"
              >
                {{ $t('重新選擇') }}
              </v-btn>
              <span>
                {{ $t('目前總共選取了{0}張照片', [files.length]) }}
                ({{ filesize(totalFileSize) }})
              </span>
              <v-btn
                color="primary"
                large
                depressed
                @click="match"
                :disabled="isLoading || files.length == 0"
              >
                {{ $t('下一步') }}
              </v-btn>
            </div>
          </v-stepper-content>

          <v-stepper-content step="2" >
            <v-alert
              color="primary"
              icon="mdi-information"
              text
            >
              {{ $t(`目前比對出{0}筆可送件辦門號, 請確認`, [matchedApplications.length]) }}
              <ol>
                <li>{{ $t('暫存門號需有簽名才能比對') }}</li>
                <li>{{ $t('單一門號照片數量,3張以上最多的7張') }}</li>
                <li>{{ $t('照片檔要符合檔名規則09XXXXXXXX') }}</li>
              </ol>
            </v-alert>

             <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">{{ $t("日期") }}</th>
                    <th class="text-left">{{ $t("仲介名") }}</th>
                    <th class="text-left">{{ $t("備註") }}</th>
                    <th class="text-left">{{ $t("證號") }}</th>
                    <th class="text-left">{{ $t("門號") }}</th>
                    <th class="text-left">{{ $t("卡號") }}</th>
                    <th class="text-left">{{ $t("狀態") }}</th>
                    <th class="text-left">{{ $t("門號類型") }}</th>
                    <th class="text-left">{{ $t("照片數") }}</th>
                    <th class="text-left">{{ $t("上傳狀態") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in matchedApplications" :key="item.id">
                    <td>{{ item.created_at | date }}</td>
                    <td>{{ item.vendor_agent ? item.vendor_agent.name : item.agency }}</td>
                    <td>{{ item.note }}</td>
                    <td>{{ item.identity }}</td>
                    <td>{{ item.sim.phone }}</td>
                    <td>{{ item.sim.card_no }}</td>
                    <td><status-chip :status="item.status"></status-chip></td>
                    <td>{{ typesMapping[item.type] }}</td>
                    <td>{{ item.imageCount }}</td>
                    <td>{{ uploadStatus[index] }}</td>
                  </tr>
                </tbody>
              </template>
             </v-simple-table>

            <div class="d-flex justify-space-between mt-12">
              <v-btn
                large
                depressed
                @click="stepBack"
                :disabled="isLoading"
              >
                {{ $t('上一步') }}
              </v-btn>
               <v-btn
                color="primary"
                large
                depressed
                :loading="isLoading"
                :disabled="hasSucceded || !matchedApplications.length"
                @click="submit"
              >
                {{ $t('送件') }}
              </v-btn>
            </div>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-col>
  </v-row>
</template>

<script>
import { date } from '../../libs/filters';
import { mapState, mapActions, mapGetters } from 'vuex';
import filesize from 'filesize';
import moment from 'moment';
import ImagesDropper from './components/ImagesDropper';
import StatusChip from '../../components/StatusChip';
import { APPLICATION_STATUS } from '../../libs/const.js';
import readAndCompressImage from '../../libs/read-and-compress-image';

export default {
  filters: {
    date,
  },

  components: {
    ImagesDropper,
    StatusChip,
  },

  data() {
    return {
      isLoading: false,
      hasSucceded: false,
      step: 1,
      files: [],
      matchedPhotoCount: 0,
      matchedApplications: [],
      uploadStatus: [],
      typesMapping: {
        apply: this.$t('一般門號'),
        upgrade: this.$t('升級'),
        change: this.$t('換補卡'),
      },
    };
  },

  computed: {
    totalFileSize() {
      return this.files.reduce((total, file) => total + file.size, 0);
    },

    ...mapState('applications', {
      applications: 'all'
    }),

    ...mapGetters(['isAbroad']),
    ...mapState(['user']),
  },

  beforeRouteEnter (to, from, next) {
    next(vm => {
      if (!vm.user.vendor.can_batch) {
        next('/');
      }
    });
  },

  beforeRouteLeave (to, from , next) {
    if (this.isLoading) {
      alert(this.$t('辦門號紀錄上傳中，請勿關閉視窗。'));
      next(false);
    }

    next();
  },

  mounted() {
    this.getApplications({
      status: APPLICATION_STATUS.ON_HOLD,
      withData: ['images'],
    });

    window.addEventListener("beforeunload", this.preventNavigation)
    this.$once("hook:beforeDestroy", () => {
      window.removeEventListener("beforeunload", this.preventNavigation);
    })
  },

  methods: {
    filesize,

    preventNavigation(event) {
      if (!this.isLoading){
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    },

    reset() {
      this.files = [];
      this.matchedApplications = [];
      this.uploadStatus = [];
      this.matchedPhotoCount = 0;
      this.batchNumber = null;
      this.hasSucceded = false;
    },

    onFileChange ($event) {
      const files = $event.target.files || $event.dataTransfer.files;
      if (!files) {
        return;
      }

      this.files = this.files.concat([...files]);
    },

    onDrop(files) {
      this.files = this.files.concat(files);
    },

    removeFile(index) {
      this.files.splice(index, 1);
    },

    stepBack() {
      if (this.hasSucceded) {
        this.reset();
      }

      this.step--;
    },

    match() {
      // Group the files and take the first 10 chars as phone number
      let files = _.groupBy(this.files, file => {
        return file.name.substring(0, 10);
      });

      this.matchedApplications = this.applications.reduce((matched, app) => {
        let uploadImages = files[app.sim.phone];

        // Check if upload images match the application
        if (!uploadImages) {
          return matched;
        }

        // Check if user signed
        if (!app.images.some(image => image.key == 'signature')) {
          return matched;
        }

        // Check if images count is right (except for the signature image)
        const imageCount = uploadImages.length + app.images.length - 1;
        const minCount = this.isAbroad ? 2 : 3;
        if (imageCount >= minCount && imageCount <= 7) {
          const application = {...app};
          application.uploadImages = uploadImages;
          application.imageCount = imageCount;
          this.matchedPhotoCount += imageCount;

          if (this.isAbroad && !application.provision_date) {
            return matched;
          }

          matched.push(application);
        }

        return matched;
      }, []);

      this.step = 2;
      this.$vuetify.goTo(0);
    },

    async submit() {
      this.isLoading = true;
      this.batchNumber = moment().format('YYYYMMDDHHmmss');
      try {
        console.group(`Batch Submit: ${this.batchNumber}`);
        for(let index = 0; index < this.matchedApplications.length; index++) {
          let application = this.matchedApplications[index];
          if (application.status != APPLICATION_STATUS.ON_HOLD) {
            continue;
          }

          if (application.provision_date) {
            application.provision_date = moment(application.provision_date).format('YYYY-MM-DD HH:mm:ss');
          }

          this.uploadStatus[index] = this.$t('上傳中');

          const app = {...await this.store(application)};
          app.imageCount = app.images.length;

          this.matchedApplications.splice(index, 1, app);
          this.uploadStatus[index] = this.$t('送件完成');
        }

        this.hasSucceded = true;
        this.setSnackbar({
          text: this.$t('大量辦門號成功!'),
        });
      } catch (error) {
        console.error(error);
        alert(this.$t("上傳失敗！請確認一下網路狀態，或是稍後再試。\n若持續有問題，請聯絡系統管理員。(請聯絡開通中心02-8227-3308分機20及21，謝謝)"));
      }

      console.groupEnd();
      this.isLoading = false;
    },

    async store(application) {
      // Optimzie the images
      let images = [];
      for (let file of application.uploadImages) {
        const imageBlob = await readAndCompressImage(file);
        images.push(imageBlob);
      }

      // Remove the sim id if the application has been created.
      let form = {...application, ...{
        sim_id: null,
        status: APPLICATION_STATUS.SUBMITTED,
        batch: this.batchNumber,
        images: images,
      }};

      console.log(`ID: ${application.id}, PHONE: ${application.sim.phone}, FILESIZE: ${filesize(images.reduce((total, file) => total + file.size, 0))}`);

      return this.updateApplication(form);
    },

    ...mapActions(['setSnackbar']),
    ...mapActions('applications', {
      getApplications: 'get',
      updateApplication: 'update',
    }),
  },
}
</script>

<style lang="scss" scoped>
.placeholder {
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5!important;
  }
}

input[type=file] {
  position: absolute;
  left: -99999px;
}
</style>
